import React, { useState, useEffect } from 'react';
import { assetsAPI, debtsAPI } from '../services/api';

const TYPES = [
  { value: 'property',   label: 'Property',   emoji: '🏠', priceLabel: 'Purchase Price', dateLabel: 'Purchase Date',  showPrice: true  },
  { value: 'investment', label: 'Investment',  emoji: '📈', priceLabel: 'Cost Basis',     dateLabel: 'Date Invested',  showPrice: true  },
  { value: 'vehicle',    label: 'Vehicle',     emoji: '🚗', priceLabel: 'Purchase Price', dateLabel: 'Purchase Date',  showPrice: true  },
  { value: 'savings',    label: 'Savings',     emoji: '💰', priceLabel: null,             dateLabel: 'Account Opened', showPrice: false },
  { value: 'other',      label: 'Other',       emoji: '📦', priceLabel: 'Original Value', dateLabel: 'Acquired Date',  showPrice: true  },
];

const TYPE_COLORS = {
  property:   '#0EBCD4',
  investment: '#a78bfa',
  vehicle:    '#34d399',
  savings:    '#fbbf24',
  other:      '#94a3b8',
};

const EMPTY_FORM = {
  name: '', type: 'other', current_value: '',
  purchase_price: '', purchase_date: '', notes: '', link: '',
};

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtFull = (n) => {
  if (n === null || n === undefined || isNaN(parseFloat(n))) return '—';
  return `$${parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Simple donut chart using SVG
function DonutChart({ data, size = 160 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={size/2 - 10} fill="none" stroke="var(--border)" strokeWidth="20"/>
    </svg>
  );

  const r = size / 2 - 14;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  let offset = 0;
  const slices = data.map((d) => {
    const pct = d.value / total;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const slice = { ...d, dash, gap, offset, pct };
    offset += dash;
    return slice;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      {slices.map((s) => (
        <circle
          key={s.label}
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={s.color}
          strokeWidth="20"
          strokeDasharray={`${s.dash} ${s.gap}`}
          strokeDashoffset={-s.offset}
          strokeLinecap="butt"
        />
      ))}
      <circle cx={cx} cy={cy} r={r - 12} fill="var(--card-bg, #1a1f2e)"/>
    </svg>
  );
}

// Simple bar chart using SVG
function BarChart({ data, height = 100 }) {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const barW = Math.floor(240 / data.length) - 6;

  return (
    <svg width="100%" height={height + 24} viewBox={`0 0 240 ${height + 24}`} preserveAspectRatio="xMidYMid meet">
      {data.map((d, i) => {
        const barH = Math.max(4, (d.value / max) * height);
        const x = i * (240 / data.length) + 3;
        const y = height - barH;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={barH} rx="3" fill={d.color} opacity="0.85"/>
            <text x={x + barW / 2} y={height + 14} textAnchor="middle" fontSize="9" fill="var(--text-muted)" fontFamily="sans-serif">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function StatWidget({ label, value, sub, subColor, children }) {
  return (
    <div style={{
      background: 'var(--card-bg, #1a1f2e)',
      border: '1px solid var(--border, #2a3042)',
      borderRadius: 12,
      padding: '20px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 0,
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary, #fff)', lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: subColor || 'var(--text-muted)', fontWeight: 500 }}>
          {sub}
        </div>
      )}
      {children}
    </div>
  );
}

function ChartWidget({ label, children }) {
  return (
    <div style={{
      background: 'var(--card-bg, #1a1f2e)',
      border: '1px solid var(--border, #2a3042)',
      borderRadius: 12,
      padding: '20px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      minWidth: 0,
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Assets() {
  const [items, setItems] = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [assetsRes, debtsRes] = await Promise.all([
        assetsAPI.getAll(),
        debtsAPI.getAll(),
      ]);
      setItems(assetsRes.data);
      const debt = debtsRes.data.reduce((s, d) => s + parseFloat(d.balance || 0), 0);
      setTotalDebt(debt);
    } catch (err) {
      console.error('Assets fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        current_value: parseFloat(formData.current_value) || 0,
        purchase_price: formData.purchase_price !== '' ? parseFloat(formData.purchase_price) : null,
        purchase_date: formData.purchase_date || null,
      };
      if (editingId) await assetsAPI.update(editingId, payload);
      else await assetsAPI.create(payload);
      fetchAll();
      resetForm();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save asset');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      type: item.type,
      current_value: String(item.current_value),
      purchase_price: item.purchase_price !== null ? String(item.purchase_price) : '',
      purchase_date: item.purchase_date ? item.purchase_date.split('T')[0] : '',
      notes: item.notes || '',
      link: item.link || '',
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this asset?')) {
      try { await assetsAPI.delete(id); fetchAll(); }
      catch (err) { console.error(err); }
    }
  };

  const resetForm = () => { setFormData(EMPTY_FORM); setEditingId(null); setShowForm(false); };
  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  // Dashboard calculations
  const totalAssets = items.reduce((s, i) => s + parseFloat(i.current_value || 0), 0);
  const netWorth = totalAssets - totalDebt;
  const totalGain = items.reduce((s, i) => {
    if (i.purchase_price === null || i.purchase_price === undefined) return s;
    return s + (parseFloat(i.current_value) - parseFloat(i.purchase_price));
  }, 0);
  const assetsWithPurchase = items.filter(i => i.purchase_price !== null && i.purchase_price !== undefined);

  // Donut data by type
  const byType = TYPES.map(t => ({
    label: t.label,
    value: items.filter(i => i.type === t.value).reduce((s, i) => s + parseFloat(i.current_value || 0), 0),
    color: TYPE_COLORS[t.value],
  })).filter(d => d.value > 0);

  // Bar data by type
  const barData = TYPES.map(t => ({
    label: t.emoji,
    value: items.filter(i => i.type === t.value).reduce((s, i) => s + parseFloat(i.current_value || 0), 0),
    color: TYPE_COLORS[t.value],
  })).filter(d => d.value > 0);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Assets</h2>
          <p>Track and manage your household assets</p>
        </div>
        <button
          onClick={() => { if (showForm && !editingId) resetForm(); else setShowForm(!showForm); }}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : '+ Add Asset'}
        </button>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Asset Name *</label>
                  <input type="text" className="form-input" value={formData.name}
                    onChange={set('name')} placeholder="e.g. Primary Home, 401k, Tesla" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={formData.type} onChange={set('type')}>
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>)}
                  </select>
                </div>
              </div>
              {(() => {
                const typeInfo = TYPES.find(t => t.value === formData.type) || TYPES[4];
                return (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Current Value *</label>
                      <input type="number" className="form-input" value={formData.current_value}
                        onChange={set('current_value')} placeholder="0.00" min="0" step="0.01" required />
                    </div>
                    {typeInfo.showPrice && (
                      <div className="form-group">
                        <label className="form-label">{typeInfo.priceLabel}</label>
                        <input type="number" className="form-input" value={formData.purchase_price}
                          onChange={set('purchase_price')} placeholder="0.00" min="0" step="0.01" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">{typeInfo.dateLabel}</label>
                      <input type="date" className="form-input" value={formData.purchase_date}
                        onChange={set('purchase_date')} />
                    </div>
                  </div>
                );
              })()}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Link</label>
                  <input type="url" className="form-input" value={formData.link}
                    onChange={set('link')} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input type="text" className="form-input" value={formData.notes}
                    onChange={set('notes')} placeholder="Optional notes" />
                </div>
              </div>
              <div className="flex gap-1">
                <button type="submit" className="btn btn-success">{editingId ? 'Update' : 'Add'}</button>
                <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <p className="empty-state-text">No assets yet</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">Add Your First Asset</button>
          </div>
        </div>
      ) : (
        <>
          {/* Stat Widgets Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
            <StatWidget
              label="Total Assets"
              value={fmt(totalAssets)}
              sub={`${items.length} asset${items.length !== 1 ? 's' : ''} tracked`}
            />
            <StatWidget
              label="Net Worth"
              value={fmt(netWorth)}
              sub={totalDebt > 0 ? `After $${(totalDebt/1000).toFixed(1)}K in debts` : 'No debts recorded'}
              subColor={netWorth >= 0 ? '#34d399' : '#f87171'}
            />
            <StatWidget
              label="Total Gain / Loss"
              value={assetsWithPurchase.length ? fmt(totalGain) : '—'}
              sub={assetsWithPurchase.length ? `Across ${assetsWithPurchase.length} asset${assetsWithPurchase.length !== 1 ? 's' : ''} with purchase price` : 'Add purchase prices to track gain/loss'}
              subColor={totalGain >= 0 ? '#34d399' : '#f87171'}
            />
          </div>

          {/* Chart Widgets Row */}
          {byType.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 16 }}>
              <ChartWidget label="Asset Breakdown">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <DonutChart data={byType} size={120} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {byType.map(d => (
                      <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                        <span style={{ color: 'var(--text-muted)' }}>{d.label}</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600, marginLeft: 'auto' }}>
                          {Math.round(d.value / totalAssets * 100)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </ChartWidget>

              <ChartWidget label="Value by Type">
                <BarChart data={barData} height={90} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                  {barData.map(d => (
                    <div key={d.label} style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {d.label} {fmt(d.value)}
                    </div>
                  ))}
                </div>
              </ChartWidget>

              <ChartWidget label="Debt vs Assets">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <DonutChart
                    data={[
                      { label: 'Assets', value: totalAssets, color: '#0EBCD4' },
                      { label: 'Debt', value: totalDebt, color: '#f87171' },
                    ].filter(d => d.value > 0)}
                    size={120}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Assets</div>
                      <div style={{ color: '#0EBCD4', fontWeight: 700, fontSize: 15 }}>{fmt(totalAssets)}</div>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Debt</div>
                      <div style={{ color: '#f87171', fontWeight: 700, fontSize: 15 }}>{fmt(totalDebt)}</div>
                    </div>
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, fontSize: 12 }}>
                      <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Net Worth</div>
                      <div style={{ color: netWorth >= 0 ? '#34d399' : '#f87171', fontWeight: 700, fontSize: 15 }}>{fmt(netWorth)}</div>
                    </div>
                  </div>
                </div>
              </ChartWidget>
            </div>
          )}

          {/* Asset List Table */}
          <div className="card">
            <div style={{
              padding: '12px 24px 8px', fontSize: 12, fontWeight: 700,
              color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              All Assets
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Asset', 'Type', 'Current Value', 'Purchase Price / Basis', 'Gain / Loss', ''].map(h => (
                      <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const gain = item.purchase_price !== null && item.purchase_price !== undefined
                      ? parseFloat(item.current_value) - parseFloat(item.purchase_price)
                      : null;
                    const typeInfo = TYPES.find(t => t.value === item.type) || TYPES[4];
                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg, rgba(255,255,255,0.03))'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {item.link
                              ? <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-teal)' }}>{item.name}</a>
                              : item.name}
                          </div>
                          {item.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.notes}</div>}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            background: `${TYPE_COLORS[item.type]}22`,
                            color: TYPE_COLORS[item.type],
                            border: `1px solid ${TYPE_COLORS[item.type]}44`,
                            borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                          }}>
                            {typeInfo.emoji} {typeInfo.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {fmtFull(item.current_value)}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>
                          {item.purchase_price !== null && item.purchase_price !== undefined ? fmtFull(item.purchase_price) : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: gain === null ? 'var(--text-muted)' : gain >= 0 ? '#34d399' : '#f87171' }}>
                          {gain === null ? '—' : `${gain >= 0 ? '+' : ''}${fmtFull(gain)}`}
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => handleEdit(item)} className="btn btn-secondary btn-sm">Edit</button>
                            <button onClick={() => handleDelete(item.id)} className="btn btn-danger btn-sm">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Assets;
